import type { AxiosInstance, AxiosRequestConfig } from "axios";

import { API_URL } from "@config/base";

import { createInstance } from "./core/instance";
import {
  AuthService,
  ConfigService,
  DelegationService,
  InstructionService,
  TransactionService,
} from "./services";

export interface OtimAPIClientConfig {
  authorizationToken?: string;
  baseURL?: string;
  withCredentials?: boolean; // for apis that require cookies
  headers?: Record<string, string>; // custom headers
  axiosConfig?: AxiosRequestConfig; // additional axios configuration
}

export class OtimAPIClient {
  private readonly clientConfig: OtimAPIClientConfig;
  private readonly instance: AxiosInstance;

  private authService?: AuthService;
  private configService?: ConfigService;
  private delegationService?: DelegationService;
  private instructionService?: InstructionService;
  private transactionService?: TransactionService;

  constructor(config: OtimAPIClientConfig = {}) {
    this.clientConfig = config;
    this.instance = createInstance({
      baseURL: this.clientConfig.baseURL ?? API_URL,
      ...(this.clientConfig.withCredentials !== undefined && {
        withCredentials: this.clientConfig.withCredentials,
      }),
      ...(this.clientConfig.headers && {
        headers: this.clientConfig.headers,
      }),
      ...this.clientConfig.axiosConfig,
    });

    if (this.clientConfig.authorizationToken) {
      this.setAuthToken(this.clientConfig.authorizationToken);
    }
  }

  get auth(): AuthService {
    if (!this.authService) {
      this.authService = new AuthService(this.instance);
    }
    return this.authService;
  }

  get config(): ConfigService {
    if (!this.configService) {
      this.configService = new ConfigService(this.instance);
    }
    return this.configService;
  }

  get delegation(): DelegationService {
    if (!this.delegationService) {
      this.delegationService = new DelegationService(this.instance);
    }
    return this.delegationService;
  }

  get instruction(): InstructionService {
    if (!this.instructionService) {
      this.instructionService = new InstructionService(this.instance);
    }
    return this.instructionService;
  }

  get transaction(): TransactionService {
    if (!this.transactionService) {
      this.transactionService = new TransactionService(this.instance);
    }
    return this.transactionService;
  }

  setAuthToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  clearAuth(): void {
    delete this.instance.defaults.headers.common.Authorization;
  }
}

export function createAPIClient(
  config: OtimAPIClientConfig = {},
): OtimAPIClient {
  return new OtimAPIClient(config);
}

export const otimAPI = createAPIClient();

export class ApiClient {
  private readonly instance: AxiosInstance;

  constructor(config: { baseURL: string }) {
    this.instance = createInstance({ baseURL: config.baseURL });
  }

  async request<T = unknown>(
    method: string,
    path: string,
    data?: unknown,
  ): Promise<T> {
    const response = await this.instance.request<T>({
      method,
      url: path,
      data,
    });
    return response.data;
  }
}
